import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { Fragment, useCallback, useMemo, useRef } from 'react';
import useWindowSize from 'react-use/lib/useWindowSize';
import { useDevicePixelRatio } from 'use-device-pixel-ratio';
import { useInstanceId } from '../../../../context/instance/instanceContext';
import { PanelHeader } from '../../../../panels/panelHeader';
import { PanelItemDivider } from '../../../../panels/panelItemDivider';
import { PanelContainer } from '../../../../panels/panelStyles';
import { useAppSelector } from '../../../../redux/store';
import { DEBUG_PANEL_BACKGROUND_COLOR, DEBUG_PANEL_LIST_BACKGROUND_COLOR } from '../../../../styles/colors';
import { PANEL_CONTENT_PADDING_STRING } from '../../../../styles/styles';
import { ALL_DEBUG_INFO_SESSION_STATS, debugInfoSessionStats } from '../../../../utils/debugInfoUtils';
import { getDeviceDetectInfo } from '../../../../utils/deviceDetectUtils';
import { getAnimationCount } from '../../../../utils/pixiUtils';
import { getBooleanString } from '../../../../utils/utils';
import { getBuildTime, getBuildTimestamp, getGitCommit } from '../../../../utils/versionUtils';
import { DebugItemContainer } from './debugItemContainer';
import { FpsCounter } from './fpsCounter';
import { HiDpiDebugItemContainer } from './hiDpiDebugItemContainer';
import { PrivateBrowserDebugItemContainer } from './privateBrowserDebugItemContainer';
import { RealTimeDebugItemContainer } from './realTimeDebugItemContainer';
const PAPER_CSS = css `
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: ${DEBUG_PANEL_LIST_BACKGROUND_COLOR} !important;
  padding: 0;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
export const DebugPanel = () => {
    const roomSceneType = useAppSelector((state) => state.scene.roomSceneType);
    const myPlayerId = useAppSelector((state) => state.selfData.id);
    const myUsername = useAppSelector((state) => state.selfData.username);
    const paperRef = useRef(null);
    const onClickCopy = useCallback(() => {
        if (paperRef.current?.textContent) {
            navigator.clipboard.writeText(paperRef.current.textContent);
        }
    }, []);
    const dpr = useDevicePixelRatio();
    const windowSize = useWindowSize();
    const instanceId = useInstanceId();
    const deviceDetectInfo = useMemo(() => getDeviceDetectInfo(), 
    // recalculate when certain things change:
    // TODO: Trigger a recalculation on screen resolution change as well.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dpr, windowSize.width, windowSize.height]);
    const items = [];
    if (myPlayerId !== undefined && myUsername !== undefined) {
        items.push(_jsx(DebugItemContainer, { title: 'User', value: `${myUsername}:${myPlayerId}` }, 'user'));
    }
    items.push(_jsx(DebugItemContainer, { title: 'FPS', value: _jsx(FpsCounter, {}) }, 'fps'), _jsx(RealTimeDebugItemContainer, { title: 'Animation Count', valueFn: () => getAnimationCount(instanceId) }), _jsx(HiDpiDebugItemContainer, {}), _jsx(DebugItemContainer, { title: 'Build Time', value: getBuildTime() }, 'build-time'), _jsx(DebugItemContainer, { title: 'Build Timestamp', value: getBuildTimestamp() }, 'build-timestamp'), _jsx(DebugItemContainer, { title: 'Git Commit', value: getGitCommit() }, 'git-commit'), _jsx(PrivateBrowserDebugItemContainer, {}));
    Object.entries(deviceDetectInfo).forEach(([name, value]) => {
        items.push(_jsx(DebugItemContainer, { title: name, value: typeof value === 'boolean' ? getBooleanString(value) : value }, name));
    });
    ALL_DEBUG_INFO_SESSION_STATS.forEach((debugInfoSessionStatName) => {
        items.push(_jsx(RealTimeDebugItemContainer, { title: debugInfoSessionStatName, valueFn: () => debugInfoSessionStats[debugInfoSessionStatName] }, debugInfoSessionStatName));
    });
    if (roomSceneType) {
        items.push(_jsx(DebugItemContainer, { title: 'Scene', value: roomSceneType }, 'scene'));
    }
    return (_jsxs(PanelContainer, { padding: PANEL_CONTENT_PADDING_STRING, "aria-label": 'debug panel', children: [_jsx(PanelHeader, { title: 'Debug', color: 'white', headerRight: _jsx(IconButton, { size: 'small', onClick: onClickCopy, title: 'Copy', color: 'inherit', children: _jsx(ContentCopyOutlinedIcon, {}) }) }), _jsx(Paper, { component: 'section', ref: paperRef, css: PAPER_CSS, children: items.map((item, i) => (_jsxs(Fragment, { children: [i > 0 && _jsx(PanelItemDivider, { color: DEBUG_PANEL_BACKGROUND_COLOR, thicknessPx: 2 }), item] }, i))) })] }));
};
